import { internalOrigins, isInternalUrl } from '@modules/common/utils/internal-url';
import { BLOCKED_IMAGE_PLACEHOLDER_DATA_URL, BLOCKED_IMAGE_TOOLTIP } from '@modules/common/utils/image-loader.util';
import {
  APP_ATTRIBUTE_PREFIX,
  createEmailSanitizer,
  isSvgResourceElement,
  setIfChanged,
  withHttpsScheme,
} from '@modules/common/utils/email-sanitizer';

/**
 * SECURITY BOUNDARY for received mail on the web. The frame this document is written into carries
 * `sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"`: it never runs
 * script, submits a form or navigates the app, and it opens or downloads only from a link the reader clicks,
 * which this builder sends to a new top-level tab with no opener and no referrer. The policy below
 * and the sanitizer are defence in depth behind that, and they are what keep the mail from loading
 * anything the reader has not agreed to (tracking pixels, CSS beacons, remote fonts).
 */
export interface EmailDocumentOptions {
  /** The reader pressed "Load images" for this message: remote images may load over https. */
  readonly allowRemoteImages: boolean;
  /** Inline (cid) image URL as written in the mail → the `blob:` URL the reader fetched it into. */
  readonly inlineImageUrls?: ReadonlyMap<string, string>;
  /**
   * Images on our own origins the reader tried and failed to fetch (or did not fetch, past its cap).
   * They are remote content: offered by "Load images", loaded by URL after consent.
   */
  readonly unresolvedInlineImages?: ReadonlySet<string>;
  /** Origins whose images are the mail's own inline attachments. Defaults to the app's origins. */
  readonly internalOrigins?: ReadonlyArray<string>;
  /**
   * After consent, hold remote `<img>` sources back (no `src`, a {@link LAZY_IMAGE_ATTRIBUTE} index into
   * `lazyImageSources`) for the reader to assign as each image nears the viewport. A full-height frame
   * would otherwise load every image at once. Print documents leave this off.
   */
  readonly lazyRemoteImages?: boolean;
}

export interface EmailDocument {
  /** A complete document for the frame's `srcdoc`, its CSP the first element of `<head>`. */
  readonly html: string;
  /**
   * The mail loads something remote that waits for consent: a remote image, a `background` attribute
   * or a CSS `url()` / `image-set()`. Drives the "Load images" banner, together with the frame's
   * `securitypolicyviolation` events for anything this static scan cannot see.
   */
  readonly remoteContent: boolean;
  /** The mail's inline images on an internal origin, once each, for the reader to fetch. */
  readonly inlineImageSources: ReadonlyArray<string>;
  /** Held-back remote image sources, by the index in each image's {@link LAZY_IMAGE_ATTRIBUTE}. */
  readonly lazyImageSources: ReadonlyArray<string>;
}

/** Marks an image whose remote source the reader assigns later; the sender's own `data-stch-*` are dropped. */
export const LAZY_IMAGE_ATTRIBUTE = `${APP_ATTRIBUTE_PREFIX}lazy`;

const CSP_BEFORE_IMAGES = ["default-src 'none'", "script-src 'none'", "style-src 'unsafe-inline'"] as const;
const CSP_AFTER_IMAGES = [
  // Remote webfonts are a render beacon that "Load images" never covers.
  'font-src data:',
  "media-src 'none'",
  "connect-src 'none'",
  "form-action 'none'",
  "frame-src 'none'",
  "child-src 'none'",
  "object-src 'none'",
  "base-uri 'none'",
] as const;

/** Only `img-src` ever loosens, and only by `https:`, only after consent. */
export function emailDocumentCsp(allowRemoteImages: boolean): string {
  return [
    ...CSP_BEFORE_IMAGES,
    allowRemoteImages ? 'img-src data: blob: https:' : 'img-src data: blob:',
    ...CSP_AFTER_IMAGES,
  ].join('; ');
}

const REMOTE_IMAGE = /^\s*(https?:)?\/\//i;
const INLINE_IMAGE = /^\s*data:/i;

const CSS_URL = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)\s]*))\s*\)/gi;
const CSS_IMAGE_SET = /image-set\(([^)]*)\)/gi;
const CSS_STRING = /"([^"]*)"|'([^']*)'/g;
/** `@import` and webfonts never load (style-src and font-src stay shut), so they ask for no consent. */
const CSS_NEVER_LOADED = /@import[^;]*;?|@font-face\s*\{[^}]*\}/gi;
const CSS_COMMENT = /\/\*[\s\S]*?\*\//g;

const CSS_PROTOCOL_RELATIVE_URL = /(url\(\s*(?:["']\s*)?)\/\/(?!\/)/gi;
const CSS_IMAGE_SET_ARGUMENTS = /(image-set\()([^)]*)\)/gi;
const CSS_PROTOCOL_RELATIVE_STRING = /(["']\s*)\/\/(?!\/)/g;

/** {@link withHttpsScheme} for every `url()` and `image-set()` string in CSS text, quotes and spacing kept. */
export function cssWithHttpsScheme(css: string): string {
  return css
    .replace(CSS_PROTOCOL_RELATIVE_URL, '$1https://')
    .replace(
      CSS_IMAGE_SET_ARGUMENTS,
      (_match, opening: string, candidates: string) =>
        `${opening}${candidates.replace(CSS_PROTOCOL_RELATIVE_STRING, '$1https://')})`,
    );
}

function isRemoteCssTarget(target: string): boolean {
  const value = target.trim();
  return value !== '' && !value.startsWith('#') && !/^(data|blob):/i.test(value);
}

const CSS_ESCAPE = /\\(?:([0-9a-f]{1,6})[ \t\n\r\f]?|([^\n\r\f0-9a-f]))/gi;
/** Text that could spell a url() or image-set() at all; anything else needs no parsing. */
const CSS_MAY_LOAD = /\\|url|image-set/i;

/** Resolves CSS escapes (`\72`, `\75 `, `\r`) as the tokenizer does; an invalid code point becomes U+FFFD. */
export function cssUnescape(css: string): string {
  return css.replace(CSS_ESCAPE, (_match, hex: string | undefined, character: string | undefined) => {
    if (hex === undefined) {
      return character ?? '';
    }
    const codePoint = Number.parseInt(hex, 16);
    const valid = codePoint > 0 && codePoint <= 0x10ffff && (codePoint < 0xd800 || codePoint > 0xdfff);
    return String.fromCodePoint(valid ? codePoint : 0xfffd);
  });
}

/** Whether CSS text references an image that would load from outside the document. */
export function cssHasRemoteUrl(css: string): boolean {
  const scanned = cssUnescape(css.replace(CSS_COMMENT, '')).replace(CSS_NEVER_LOADED, '');
  for (const [, doubleQuoted, singleQuoted, bare] of scanned.matchAll(CSS_URL)) {
    if (isRemoteCssTarget(doubleQuoted ?? singleQuoted ?? bare ?? '')) {
      return true;
    }
  }
  for (const [, candidates] of scanned.matchAll(CSS_IMAGE_SET)) {
    for (const [, doubleQuoted, singleQuoted] of candidates.matchAll(CSS_STRING)) {
      if (isRemoteCssTarget(doubleQuoted ?? singleQuoted ?? '')) {
        return true;
      }
    }
  }
  return false;
}

/** Whether declarations, as the browser's parser normalized them (`url("…")`), load a remote image. */
export function declarationsLoadRemote(style: CSSStyleDeclaration): boolean {
  for (let index = 0; index < style.length; index++) {
    if (cssHasRemoteUrl(style.getPropertyValue(style.item(index)))) {
      return true;
    }
  }
  return false;
}

function hasDeclarations(rule: CSSRule): rule is CSSRule & { readonly style: CSSStyleDeclaration } {
  return 'style' in rule;
}

function hasChildRules(rule: CSSRule): rule is CSSRule & { readonly cssRules: CSSRuleList } {
  return 'cssRules' in rule;
}

/** Walks parsed rules (grouping rules and keyframes included) for a remote image. */
export function cssRulesLoadRemote(rules: CSSRuleList): boolean {
  for (const rule of Array.from(rules)) {
    // Webfonts and imports never load in the frame (font-src data:, style-src without hosts).
    if (/^\s*@(font-face|import)\b/i.test(rule.cssText)) {
      continue;
    }
    if ((hasDeclarations(rule) && declarationsLoadRemote(rule.style)) || (hasChildRules(rule) && cssRulesLoadRemote(rule.cssRules))) {
      return true;
    }
  }
  return false;
}

let parserDocument: Document | null = null;

/** A detached document: its elements and style sheets are parsed but never rendered or fetched for. */
function cssParserDocument(): Document {
  parserDocument ??= document.implementation.createHTMLDocument('');
  return parserDocument;
}

/** A style attribute through the browser's own CSS parser. */
function styleAttributeLoadsRemote(text: string): boolean {
  const probe = cssParserDocument().createElement('div');
  probe.setAttribute('style', text);
  return declarationsLoadRemote(probe.style);
}

/** A `<style>` text through the browser's own CSS parser: a constructed sheet, else a detached one. */
function styleSheetLoadsRemote(css: string): boolean {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    return cssRulesLoadRemote(sheet.cssRules);
  } catch {
    const style = cssParserDocument().createElement('style');
    style.textContent = css;
    cssParserDocument().head.append(style);
    try {
      return style.sheet ? cssRulesLoadRemote(style.sheet.cssRules) : false;
    } finally {
      style.remove();
    }
  }
}

/**
 * The "Load images" scan for CSS text: escapes resolved for the static pattern, then — for text
 * that could spell a url() at all — the browser's own parser, whose normalized values catch any
 * spelling it accepts. The frame's CSP blocks these either way; this keeps the offer.
 */
function cssLoadsRemote(css: string, parse: (css: string) => boolean): boolean {
  return cssHasRemoteUrl(css) || (CSS_MAY_LOAD.test(css) && parse(css));
}

interface BuildState {
  readonly options: EmailDocumentOptions;
  readonly origins: ReadonlyArray<string>;
  readonly inlineSources: Set<string>;
  readonly lazySources: string[];
  remoteContent: boolean;
}

const MATHML_NAMESPACE = 'http://www.w3.org/1998/Math/MathML';
const LINK_ATTRIBUTES = ['href', 'xlink:href'] as const;

/** An HTML `<a>` or `<area>`, or an SVG `<a>`, that kept a URL through the sanitizer. */
function isNavigableLink(element: Element): boolean {
  const name = element.localName.toLowerCase();
  return (name === 'a' || name === 'area') && LINK_ATTRIBUTES.some((attribute) => element.hasAttribute(attribute));
}

/**
 * The browser opens the link natively in a new top-level tab (the frame allows popups, never scripts), whatever
 * target the sender gave: never the frame itself, the app's tab or a named window, and with no `window.opener`
 * back into the frame, whose document has the app's origin. `download` is dropped so a file link opens that tab too
 * instead of saving in place from the frame.
 */
function openInNewTab(link: Element): void {
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  link.removeAttribute('download');
}

function blockImage(image: Element): void {
  image.setAttribute('src', BLOCKED_IMAGE_PLACEHOLDER_DATA_URL);
  image.setAttribute('title', BLOCKED_IMAGE_TOOLTIP);
  image.setAttribute('data-stch-blocked', '');
}

function processImage(image: Element, current: BuildState): void {
  const source = image.getAttribute('src');
  if (source === null || INLINE_IMAGE.test(source)) {
    return;
  }
  let remote: string;
  if (isInternalUrl(source, current.origins)) {
    current.inlineSources.add(source);
    const blobUrl = current.options.inlineImageUrls?.get(source);
    if (blobUrl) {
      image.setAttribute('src', blobUrl);
      return;
    }
    if (!current.options.unresolvedInlineImages?.has(source)) {
      // Not fetched yet: a quiet placeholder until the reader's prefetch answers.
      blockImage(image);
      return;
    }
    // On our origin, but the reader could not fetch it: like any remote image it waits for consent,
    // then loads by its absolute URL (the frame's base is opaque). Our origin never joins the CSP.
    remote = new URL(source, window.location.href).href;
  } else if (REMOTE_IMAGE.test(source)) {
    remote = withHttpsScheme(source);
  } else {
    // cid:, about: and the like cannot load in the frame; show what a blocked image shows.
    blockImage(image);
    return;
  }
  current.remoteContent = true;
  if (current.options.allowRemoteImages && current.options.lazyRemoteImages) {
    image.removeAttribute('src');
    image.setAttribute(LAZY_IMAGE_ATTRIBUTE, String(current.lazySources.push(remote) - 1));
  } else if (current.options.allowRemoteImages) {
    image.setAttribute('src', remote);
    image.setAttribute('loading', 'lazy');
  } else {
    blockImage(image);
  }
}

/** Each `<style>` block: protocol-relative URLs get https; the remote scan may offer "Load images". */
function readerStyleElement(style: Element, current: BuildState): void {
  const css = style.textContent ?? '';
  const normalized = cssWithHttpsScheme(css);
  if (normalized !== css) {
    style.textContent = normalized;
  }
  if (!current.remoteContent && cssLoadsRemote(normalized, styleSheetLoadsRemote)) {
    current.remoteContent = true;
  }
}

/** The reader's own work on each element that stays, after the shared profile's. */
function readerElement(element: Element, current: BuildState): void {
  if (element.tagName === 'IMG') {
    processImage(element, current);
  }
  if (isSvgResourceElement(element)) {
    // Blocked by the policy until consent; the banner must offer it.
    for (const attribute of ['href', 'xlink:href']) {
      const source = element.getAttribute(attribute);
      if (source === null) {
        continue;
      }
      setIfChanged(element, attribute, source, withHttpsScheme(source));
      if (isRemoteCssTarget(source)) {
        current.remoteContent = true;
      }
    }
  } else if (isNavigableLink(element)) {
    openInNewTab(element);
  } else if (element.namespaceURI === MATHML_NAMESPACE) {
    // MathML `href` has been a link in Firefox, with no target to send it out of the frame: plain text here.
    LINK_ATTRIBUTES.forEach((attribute) => element.removeAttribute(attribute));
  }
  const background = element.getAttribute('background');
  if (background !== null) {
    setIfChanged(element, 'background', background, withHttpsScheme(background));
    if (isRemoteCssTarget(background)) {
      current.remoteContent = true;
    }
  }
  const style = element.getAttribute('style');
  if (style) {
    const normalized = cssWithHttpsScheme(style);
    setIfChanged(element, 'style', style, normalized);
    if (!current.remoteContent && cssLoadsRemote(normalized, styleAttributeLoadsRemote)) {
      current.remoteContent = true;
    }
  }
}

/** The shared profile, keeping `<style>` blocks and positioning: the frame's CSP and sandbox confine them. */
const readerSanitizer = createEmailSanitizer({ styleElements: true, keepPositioning: true });

/**
 * What the app's styles gave the mail when it rendered inline, now that they no longer reach it: the
 * app's text colour, font and size, visible links, list markers, and a cap on images and placeholders.
 * The sender's own CSS comes later in the document and wins.
 */
const BASE_STYLES = `html{color-scheme:light;background:#fff}
body{margin:0;color:#162739;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;font-size:14px;line-height:24px;font-weight:400;overflow-wrap:break-word}
*,::before,::after{box-sizing:content-box}
a{color:#154ccc;text-decoration:underline}
ul,ol{margin:1em 0;padding-left:40px}
li ul,li ol{margin:0}
ul{list-style-type:disc}
ul ul{list-style-type:circle}
ul ul ul{list-style-type:square}
ol{list-style-type:decimal}
table{border-collapse:separate;text-indent:initial}
img{max-width:100%;height:auto}
img[data-stch-blocked]:not([height]):not([style*="height"]){max-height:120px}`;

/**
 * Set by the reader on the frame's `<html>` when the content is taller than its height cap: only then
 * may the frame document scroll itself. Under the app's prefix, so a sender cannot set it.
 */
export const FRAME_SCROLL_ATTRIBUTE = `${APP_ATTRIBUTE_PREFIX}frame-scroll`;

/**
 * After the sender's CSS. The frame is sized to its content, so a body pinned to the viewport
 * (`height:100%`, `min-height:100vh`) would chase its own frame, and the frame document never shows a
 * scrollbar of its own beside the reader's (unless capped, see {@link FRAME_SCROLL_ATTRIBUTE}).
 * Knot highlights stay visible.
 */
const TRAILING_STYLES = `html,body{height:auto!important;min-height:0!important;max-height:none!important}
html:not([${FRAME_SCROLL_ATTRIBUTE}]),html:not([${FRAME_SCROLL_ATTRIBUTE}])>body{overflow:hidden!important}
span.stch-knot-mark{background-color:#fffa66!important;color:inherit!important;padding:0!important}`;

/** The class mark.js wraps knot matches in, painted by {@link TRAILING_STYLES}. */
export const KNOT_MARK_CLASS = 'stch-knot-mark';

/**
 * Builds the reader's frame document for a received mail. Pure: the same input always gives the same
 * document, and consent is a rebuild with `allowRemoteImages`, never a change to a live DOM.
 */
export function buildEmailDocument(bodyHtml: string, options: EmailDocumentOptions): EmailDocument {
  const current: BuildState = {
    options,
    origins: options.internalOrigins ?? internalOrigins(),
    inlineSources: new Set<string>(),
    lazySources: [],
    remoteContent: false,
  };
  const safe = readerSanitizer.toHtml(bodyHtml, {
    element: (element) => readerElement(element, current),
    styleElement: (style) => readerStyleElement(style, current),
  });

  // Injected after sanitizing: DOMPurify drops <meta>, so the sender cannot occupy these slots.
  const html =
    '<!doctype html><html><head>' +
    `<meta http-equiv="Content-Security-Policy" content="${emailDocumentCsp(options.allowRemoteImages)}">` +
    '<meta name="referrer" content="no-referrer">' +
    `<style>${BASE_STYLES}</style>` +
    `</head><body>${safe}<style>${TRAILING_STYLES}</style></body></html>`;

  return {
    html,
    remoteContent: current.remoteContent,
    inlineImageSources: Array.from(current.inlineSources),
    lazyImageSources: current.lazySources,
  };
}
